import React from 'react';
import { Clock, Send, Save } from 'lucide-react';
import type { InvoiceStatus } from '@dutchinvoice/types';
import { useTranslation } from '../../hooks/useTranslation';

type StatusConfigItem = {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  textKey: string;
  className: string;
  hidden?: boolean;
};

const STATUS_CONFIG: Record<string, StatusConfigItem> = {
  concept: {
    icon: Clock,
    textKey: 'status.concept',
    className: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
  },
  sent: {
    icon: Send,
    textKey: 'status.sent',
    className: 'bg-green-50 text-green-600 hover:bg-green-100'
  },
  saved: {
    icon: Save,
    textKey: 'status.saved',
    className: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
    hidden: true
  }
};

interface StatusBadgeProps {
  status: InvoiceStatus;
  onClick?: () => void;
  asChild?: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  status, 
  onClick, 
  asChild = false 
}) => {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[status];

  if (!config) {
    console.error(`Invalid invoice status: ${status}`);
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          <Clock className="w-3 h-3" />
          {status}
        </span>
      </div>
    );
  }

  if (config.hidden) {
    return null;
  }

  const { icon: Icon, textKey, className } = config;
  const sharedProps = {
    onClick,
    className: `inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${className} ${onClick ? 'cursor-pointer' : 'cursor-default'}`
  };

  const content = (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
        <Icon className="w-3 h-3" />
        {t(textKey as any)}
      </span>
    </div>
  );

  if (asChild) {
    return content;
  }

  return (
    <div {...sharedProps}>
      {content}
    </div>
  );
}; 